// First: later modules call built-ins that older Safari lacks.
import './polyfills';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
//import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { Auth0Provider } from '@auth0/auth0-react';
import { Auth0BackedProvider, NoAuthProvider } from './context/AuthContext';
import { isMockData } from './lib/demo/mode';
import { installCrashReporting } from './lib/errorReport';
import { ErrorBoundary } from './components/common/error-screen';
import { finishLogin } from './lib/authRedirect';
// Call the element loader after the platform has been bootstrapped
//defineCustomElements(window);

const isPrivateIP = /^(localhost|10\.|172\.(1[6-9]|2[0-9]|3[01])\.|192\.168\.|100\.(6[4-9]|[7-9][0-9]|1([01][0-9]|2[0-7]))\.)/.test(
  window.location.hostname
);
// A demo build showing mock data. `?data=live` on the same build reaches the
// real API and needs a real login — see lib/demo/mode.ts.
const isMockDemo = Boolean(import.meta.env.VITE_DEMO_MODE) && isMockData();

// A mock demo has no backend to authenticate against and is not a private IP,
// so it would otherwise be sent to a login it can never complete. A demo build
// asked for live data does need Auth0, which is the point of the switch.
const useExternalAuth =
  !(window as any).__E2E_NO_AUTH__ && !isPrivateIP && !isMockDemo;

installCrashReporting();

const root = createRoot(document.getElementById('root')!);

const render = () => {
  // Tells index.html's boot fallback the app started: crashes from here on
  // are the ErrorBoundary's.
  (window as any).__swStarted = true;
  root.render(
  <React.StrictMode>
    <ErrorBoundary>
    {useExternalAuth ? (
      <Auth0Provider
        domain="dev-s4l4fwvt05o1jmep.eu.auth0.com"
        clientId="vgN35I6xG7uATYnQ5ExDSv6GpXdkx5VS"
        cacheLocation='localstorage'
        // Safari blocks the third-party cookie the hidden-iframe renewal
        // needs, so on iPhone a session ended at the first token expiry.
        // A refresh token renews without it; the iframe stays as a fallback
        // for when Auth0 issues none.
        useRefreshTokens
        useRefreshTokensFallback
        onRedirectCallback={finishLogin}
        authorizationParams={{
          scope: 'openid profile email offline_access',
          audience: 'https://sizewall-function-api.azurewebsites.net/',
          redirect_uri: window.location.origin,
        }}
      >
        <Auth0BackedProvider>
          <App />
        </Auth0BackedProvider>
      </Auth0Provider>
    ) : (
      <NoAuthProvider>
        <App />
      </NoAuthProvider>
    )}
    </ErrorBoundary>
  </React.StrictMode>
);
};

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
/**
 * The demo backend is installed before the first render, because
 * ClientDataHolder fires on mount and an un-intercepted request would leave
 * the origin. The import is dynamic so none of the demo dataset reaches a
 * production bundle.
 */
// The condition is the env literal, not the derived const: Rollup folds
// `import.meta.env.VITE_DEMO_MODE` to undefined in a normal build and drops
// the whole branch, so the dataset never reaches a production bundle. Going
// through Boolean() first leaves the chunk behind, unfetched but present.
if (import.meta.env.VITE_DEMO_MODE && isMockData()) {
  import('./lib/demo/install')
    .then(({ installDemoBackend }) => installDemoBackend())
    .finally(render);
} else {
  render();
}

serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
